import { warn } from './warn';

import type {
  YandexMetricaCounter,
  YandexMetricaProperties,
} from '../YandexMetricaManager.types';

const METRICA_TAG_URL = 'https://mc.yandex.ru/metrika/tag.js';

const COUNTER_OPTIONS = {
  clickmap: true,
  trackLinks: true,
  accurateTrackBounce: true,
  webvisor: true,
};

/**
 * Заглушка счётчика: копит вызовы до загрузки `tag.js`, который потом
 * проигрывает очередь `ym.a` и подменяет себя настоящей реализацией.
 */
const createCounterStub = (): YandexMetricaCounter => {
  const counter: YandexMetricaCounter = (...parameters: unknown[]) => {
    counter.a ??= [];
    counter.a.push(parameters);
  };
  counter.l = Date.now();

  return counter;
};

/**
 * Признак «тег уже запрашивали» — сам тег в DOM, а не отдельный флаг и не
 * наличие `ym`. Заглушка появляется раньше тега и переживает неудачную
 * загрузку, поэтому по ней нельзя судить, загружен ли счётчик.
 */
const isCounterTagRequested = () =>
  [...document.querySelectorAll<HTMLScriptElement>('script')].some(
    (script) => script.src === METRICA_TAG_URL,
  );

/**
 * Тег не загрузился — убираем его из DOM. Очередь `ym` при этом сохраняем:
 * следующая инициализация (ремоунт менеджера при логине с публичной страницы)
 * вставит тег заново, и `tag.js` проиграет накопленные вызовы.
 */
const handleCounterTagFailure = (scriptElement: HTMLScriptElement) => {
  warn(`failed to load ${METRICA_TAG_URL}`);
  scriptElement.remove();
};

const appendCounterTag = () => {
  const scriptElement = document.createElement('script');
  scriptElement.async = true;
  scriptElement.src = METRICA_TAG_URL;
  scriptElement.addEventListener(
    'error',
    () => handleCounterTagFailure(scriptElement),
    { once: true },
  );
  document.head.append(scriptElement);
};

/**
 * Инициализирует Яндекс Метрику без inline-скрипта: сам код живёт в бандле
 * (`script-src 'self'`), а `tag.js` подключается внешним тегом с уже
 * разрешённого `mc.yandex.ru`. Из-за этого политике не нужен `'unsafe-inline'`.
 *
 * @example
 * initYandexMetrica({ id: '21803329' });
 */
export const initYandexMetrica = ({ id }: YandexMetricaProperties) => {
  if (!id) {
    warn('id is required');
    return;
  }

  if (!globalThis.ym) {
    // Контракт вендора: `tag.js` ищет счётчик именно в глобальной `ym`.
    // eslint-disable-next-line unicorn/no-global-object-property-assignment
    globalThis.ym = createCounterStub();
  }

  if (!isCounterTagRequested()) {
    appendCounterTag();
  }

  globalThis.ym(id, 'init', COUNTER_OPTIONS);
};
